import {
  appUserSchema,
  authSessionResponseSchema,
  devHrLinkResponseSchema,
  pageResponseSchema,
  purgeUserResponseSchema,
  rolesResponseSchema,
  userLookupResponseSchema,
  type CreateRoleRequest,
  type CreateUserRequest,
  type ListUsersQuery,
  type UpdateRoleRequest,
} from '@kitobxonlik/contracts';
import { api, cleanParams } from './client';

/**
 * Kirish/chiqish va ruxsatlarni boshqarish.
 *
 * Tokenlar javobda QAYTMAYDI — backend `Set-Cookie` (httpOnly) qo'yadi,
 * brauzer keyingi so'rovlarga o'zi qo'shadi (`withCredentials: true`).
 */
export const authApi = {
  /** 1-yo'l: OneID (SSO) callback'idan kelgan `code` */
  oneId: async (code: string, codeVerifier: string, redirectUri: string) =>
    authSessionResponseSchema.parse(
      (
        await api.post('/v1/auth/one-id', {
          code,
          code_verifier: codeVerifier,
          redirect_uri: redirectUri,
        })
      ).data,
    ),

  /** 2-yo'l: HR portalidagi tugmadan kelgan shifrlangan `?p=` */
  hrEntry: async (p: string) =>
    authSessionResponseSchema.parse((await api.post('/v1/auth/hr-entry', { p })).data),

  /** Access token muddati tugaganda — refresh cookie bilan */
  refresh: async () => authSessionResponseSchema.parse((await api.post('/v1/auth/refresh')).data),

  logout: () => api.post('/v1/auth/logout'),

  /** Faqat lokal muhitda (backend `ENVIRONMENT=local` bo'lmasa 404) */
  devLogin: async (pinfl: string) =>
    authSessionResponseSchema.parse((await api.post('/v1/auth/dev-login', { pinfl })).data),

  /**
   * Faqat lokal: HR'dan o'tish havolasining `?p=` qiymati.
   * Shifr SERVERDA yasaladi — umumiy sir brauzer bundle'iga tushmasligi uchun.
   */
  devHrLink: async (pinfl: string) =>
    devHrLinkResponseSchema.parse((await api.post('/v1/auth/dev-hr-link', { pinfl })).data).p,
};

const usersPageSchema = pageResponseSchema(appUserSchema);

/** Rollar va ularning ruxsatlari (admin) */
export const rolesApi = {
  list: async () => rolesResponseSchema.parse((await api.get('/admin/v1/roles')).data),

  create: (input: CreateRoleRequest) => api.post('/admin/v1/roles', input),

  update: (code: string, input: UpdateRoleRequest) => api.put(`/admin/v1/roles/${code}`, input),

  remove: (code: string) => api.delete(`/admin/v1/roles/${code}`),

  /** Ruxsatlar to'plamini TO'LIQ almashtiradi */
  setPermissions: (code: string, permissions: string[]) =>
    api.put(`/admin/v1/roles/${code}/permissions`, { permissions }),
};

/** Foydalanuvchilar — qo'shish, rol biriktirish (admin) */
export const appUsersApi = {
  list: async (params: ListUsersQuery) =>
    usersPageSchema.parse(
      (await api.get('/admin/v1/users', { params: cleanParams(params as Record<string, unknown>) }))
        .data,
    ),

  /** Qo'shishdan oldin PINFL'ni HR'da tekshirish — bazaga yozmaydi */
  lookup: async (pinfl: string) =>
    userLookupResponseSchema.parse((await api.post('/admin/v1/users/lookup', { pinfl })).data),

  /** HR'dagi xodimni rollari bilan qo'shish (HR qayta tekshiriladi) */
  create: async (input: CreateUserRequest) =>
    appUserSchema.parse((await api.post('/admin/v1/users', input)).data),

  setRoles: (id: number, roles: string[]) => api.put(`/admin/v1/users/${id}/roles`, { roles }),

  setStatus: (id: number, isActive: boolean) =>
    api.patch(`/admin/v1/users/${id}/status`, { is_active: isActive }),

  /** O'chirish (soft delete, bazada qoladi) — faqat DEVELOPER (`user:purge`) */
  purge: async (id: number) =>
    purgeUserResponseSchema.parse((await api.delete(`/admin/v1/users/${id}`)).data),
};
